'use client';

import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { StrategyLoadingState } from '../../../StrategyLoadingState';
import { useProfileStrategies } from 'src/hooks/strategy';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useSite } from 'src/context/providers/SiteProvider';
import { useLogging } from 'src/context/logging';
import { useGetEngageStrategyListMembersQuery, useGetEngageStrategyDetailsQuery } from 'src/api/redux/strategy/engageStrategyApi';
import { useGetStrategyMetricsQuery } from 'src/api/redux/strategy/strategyMetricsApi';
import { useParams } from 'next/navigation';
import { useDebounce } from 'use-debounce';
import {
  StrategyResultsHeader,
  ResultsPagination,
  AddToListSection,
} from '../../../StrategyProfileResults/components';
import { StrategyMemberCard } from 'src/components/Strategy/shared';

export interface ChildRenderProps {
  member: any;
  isActive: boolean;
  onSelect: () => void;
  onHover: () => void;
  onHoverEnd: () => void;
}

interface StrategyResultsPanelProps {
  engageStrategyId: string;
  pageSize?: number;
  children?: (props: ChildRenderProps) => ReactNode;
  setShowAddToListDialog: (show: boolean) => void;
}

const StrategyResultsPanel: React.FC<StrategyResultsPanelProps> = ({
  engageStrategyId,
  pageSize = 50,
  children: _children,
  setShowAddToListDialog,
}) => {
  const { id } = useParams<{ id: string }>();
  const log = useLogging();

  // Get shared state from Redux
  const {
    search,
    setSearch,
    sortValues,
    setSortValues,
    currentPage,
    setCurrentPage,
    activeMarker,
    setActiveMarker,
    selectedProviders,
    setSelectedProviders,
    setSelectedProvider,
    bounds,
    selectedSpecialties,
    selectedStatuses,
    selectedProviderLists,
    metricToggles,
  } = useProfileStrategies();

  const { closeAllButCurrent } = useDrawer();
  const { dispatch } = useSite();

  // Local UI state
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [currentPageSelected, setCurrentPageSelected] = useState(false);

  // Debounce search for API
  const [debouncedSearch] = useDebounce(search, 300);

  // Fetch strategy details for direction
  const { data: engageStrategyDetails } = useGetEngageStrategyDetailsQuery(
    engageStrategyId,
    { skip: !engageStrategyId }
  );

  // Fetch strategy metrics
  const { data: strategyMetrics } = useGetStrategyMetricsQuery(
    {
      strategyType: 'engage',
      strategyId: engageStrategyId,
    },
    { skip: !engageStrategyId }
  );

  // Track if we've already set the default sort
  const hasSetDefaultSort = useRef(false);

  // Set default sort to first metric's first display option once loaded
  useEffect(() => {
    if (
      !hasSetDefaultSort.current &&
      strategyMetrics?.results?.length > 0 &&
      sortValues.sort === 'date_added'
    ) {
      const firstMetric = strategyMetrics.results[0];
      if (firstMetric.display_options?.length > 0) {
        hasSetDefaultSort.current = true;
        setSortValues({
          sort: 'metric',
          order: 'desc',
          sort_metric: firstMetric.display_options[0].code,
        });
      }
    }
  }, [strategyMetrics, sortValues.sort]);

  // Convert bounds for API
  const apiQueryBounds = useMemo(() => {
    if (!bounds || (bounds.min_lat === 0 && bounds.max_lat === 0)) return null;
    return {
      north: bounds.max_lat,
      south: bounds.min_lat,
      east: bounds.max_lng,
      west: bounds.min_lng,
    };
  }, [bounds]);

  // Fetch list members
  const { data: listMembersResponse, isFetching } =
    useGetEngageStrategyListMembersQuery(
      {
        strategyId: engageStrategyId,
        provider_id: id,
        q: debouncedSearch,
        page: currentPage,
        pageSize,
        order: sortValues.order,
        sort: sortValues.sort,
        ...(sortValues.sort_metric && { sort_metric: sortValues.sort_metric }),
        bounds: apiQueryBounds,
        specialties: selectedSpecialties,
        statuses: selectedStatuses,
        provider_lists: selectedProviderLists,
      },
      { skip: !engageStrategyId || !id }
    );

  // Sort options
  const sortOptions = useMemo(() => {
    const options: Array<{
      label: string;
      value: string;
      order: 'asc' | 'desc';
      metricId?: string;
    }> = [
      { label: 'Date Added (Newest)', value: 'date_added', order: 'desc' },
      { label: 'Date Added (Oldest)', value: 'date_added', order: 'asc' },
    ];

    if (strategyMetrics?.results?.length > 0) {
      const firstMetric = strategyMetrics.results[0];
      // Use first display_option label if available, otherwise fall back to metric label
      const displayLabel =
        firstMetric.display_options?.[0]?.label || firstMetric.label;
      options.push(
        {
          label: `${displayLabel} (Highest)`,
          value: 'metric',
          order: 'desc',
          metricId: firstMetric.id.toString(),
        },
        {
          label: `${displayLabel} (Lowest)`,
          value: 'metric',
          order: 'asc',
          metricId: firstMetric.id.toString(),
        }
      );
    }

    return options;
  }, [strategyMetrics]);

  const handleSortChange = (
    field: string,
    order: 'asc' | 'desc',
    metricId?: string
  ) => {
    let metricCode: string | null = null;

    if (field === 'metric' && metricId) {
      const metric = strategyMetrics?.results?.find(
        (m: any) => m.id.toString() === metricId
      );

      if (metric?.display_options?.length > 0) {
        const selectedDisplayOption = metricToggles?.[metricId];
        const validOption = metric.display_options.find(
          (option: any) => option.code === selectedDisplayOption
        );
        metricCode = validOption
          ? validOption.code
          : metric.display_options[0].code;
      }
    }

    setSortValues({
      sort: field === 'metric' ? 'metric' : field,
      order,
      sort_metric: field === 'metric' ? metricCode : null,
    });
    setIsSortDropdownOpen(false);
  };

  const currentSortLabel = useMemo(() => {
    if (sortValues.sort === 'date_added') {
      return sortValues.order === 'desc'
        ? 'Date Added (Newest)'
        : 'Date Added (Oldest)';
    }
    if (sortValues.sort === 'metric' && sortValues.sort_metric) {
      // Find the display label from the first metric's display_options
      const firstMetric = strategyMetrics?.results?.[0];
      const displayLabel = firstMetric?.display_options?.[0]?.label || 'Metric';
      return sortValues.order === 'desc'
        ? `${displayLabel} (Highest)`
        : `${displayLabel} (Lowest)`;
    }
    return 'Date Added (Newest)';
  }, [sortValues, strategyMetrics]);

  const handleAddToLists = () => {
    setShowAddToListDialog(true);
  };

  const handleCardSelect = (member: any) => {
    setSelectedProvider(member);
    setActiveMarker(member.provider_id?.toString());
    dispatch({
      type: 'SET_CURRENT_PROVIDER',
      payload: { id: member.provider_id, type: member.type || null },
    });
    closeAllButCurrent('profile');
  };

  const handleCheckboxToggle = (member: any) => {
    const currentSelected = selectedProviders || [];
    const isAlreadySelected = currentSelected.some(
      (p: MedScout.Opportunity) => p.provider_id === member.provider_id
    );

    if (isAlreadySelected) {
      setSelectedProviders(
        currentSelected.filter(
          (p: MedScout.Opportunity) => p.provider_id !== member.provider_id
        )
      );
    } else {
      setSelectedProviders([...currentSelected, member]);
    }
  };

  // Computed values
  const listMembers = useMemo(
    () => listMembersResponse?.results || [],
    [listMembersResponse?.results]
  );

  // Track whether all items on current page are selected
  useEffect(() => {
    if (listMembers.length === 0) {
      setCurrentPageSelected(false);
      return;
    }
    const allCurrentPageSelected = listMembers.every((member: any) =>
      (selectedProviders || []).some(
        (p: MedScout.Opportunity) => p.provider_id === member.provider_id
      )
    );
    setCurrentPageSelected(allCurrentPageSelected);
  }, [listMembers, selectedProviders]);

  const handleSelectAllResults = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const isChecked = e.target.checked;
      setCurrentPageSelected(isChecked);

      if (isChecked) {
        log.event('strategyMultiSelectModeActivated');
        // Add current page results to existing selections, avoiding duplicates
        const currentSelected = selectedProviders || [];
        const newSelections = [...currentSelected];
        listMembers.forEach((member: any) => {
          if (
            !currentSelected.some(
              (p: MedScout.Opportunity) => p.provider_id === member.provider_id
            )
          ) {
            newSelections.push(member);
          }
        });
        setSelectedProviders(newSelections);
      } else {
        // Only remove the current page items from selections
        setSelectedProviders(
          (selectedProviders || []).filter(
            (provider: MedScout.Opportunity) =>
              !listMembers.some(
                (member: any) => member.provider_id === provider.provider_id
              )
          )
        );
      }
    },
    [listMembers, log, selectedProviders, setSelectedProviders]
  );
  const totalCount = listMembersResponse?.count || 0;
  const numberOfPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setIsPageDropdownOpen(false);
  };

  return (
    <aside
      className="w-96 h-full bg-neutral-50 z-10 pointer-events-auto rounded-br-lg"
      aria-label="Strategy results panel"
      data-testid="strategy-results-panel"
    >
      <div className="h-full inline-flex flex-col items-start overflow-hidden">
        <div className="w-96 flex-1 bg-neutral-50 flex flex-col items-start overflow-hidden">
          <StrategyResultsHeader
            displayCount={totalCount}
            isFetching={isFetching}
            search={search}
            setSearch={setSearch}
            sortOptions={sortOptions}
            currentSortLabel={currentSortLabel}
            handleSortChange={handleSortChange}
            sortValues={sortValues}
            strategyMetrics={strategyMetrics}
            isSortDropdownOpen={isSortDropdownOpen}
            setIsSortDropdownOpen={setIsSortDropdownOpen}
            currentPageSelected={currentPageSelected}
            selectedCount={(selectedProviders || []).length}
            onSelectPageChange={handleSelectAllResults}
          />

          <div
            className="w-full flex-1 overflow-y-auto"
            role="region"
            aria-label="Strategy results list"
            data-testid="strategy-results-list"
          >
            {isFetching ? (
              <StrategyLoadingState size="md" />
            ) : (
              <ul
                className="flex flex-col gap-2 px-4 pb-4"
                role="list"
                aria-label="Opportunities"
              >
                {listMembers.length > 0 ? (
                  listMembers.map((member: any) => {
                    const isChecked = (selectedProviders || []).some(
                      (p: MedScout.Opportunity) =>
                        p.provider_id === member.provider_id
                    );
                    return (
                      <li key={member.id || member.provider_id} role="listitem">
                        <StrategyMemberCard
                          member={member}
                          type={engageStrategyDetails?.type}
                          strategyMetrics={strategyMetrics}
                          isActive={
                            activeMarker === member.provider_id?.toString()
                          }
                          isChecked={isChecked}
                          onSelect={() => handleCardSelect(member)}
                          onCheckboxChange={() => handleCheckboxToggle(member)}
                          onHover={() =>
                            setActiveMarker(member.provider_id?.toString())
                          }
                          onHoverEnd={() => setActiveMarker(null)}
                          showAddToList
                        />
                      </li>
                    );
                  })
                ) : (
                  <li
                    className="flex items-center justify-center h-32 text-neutral-500"
                    role="status"
                    data-testid="strategy-results-empty"
                  >
                    No opportunities found
                  </li>
                )}
              </ul>
            )}
          </div>

          <AddToListSection
            selectedProviders={selectedProviders}
            handleAddToLists={handleAddToLists}
          />

          <ResultsPagination
            currentPage={currentPage}
            numberOfPages={numberOfPages}
            totalCount={totalCount}
            startIndex={startIndex}
            endIndex={endIndex}
            displayCount={totalCount}
            handlePageChange={handlePageChange}
            isPageDropdownOpen={isPageDropdownOpen}
            setIsPageDropdownOpen={setIsPageDropdownOpen}
          />
        </div>
      </div>

    </aside>
  );
};

export default StrategyResultsPanel;
